import {useEffect, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {api} from "../services/apiService";
import {API_BASE_URL} from "../config";
import {useIsMobile} from "../utils/helpers";

export function useBookingDetails() {
    const params = useParams();
    const navigate = useNavigate();
    const [reservation, setReservation] = useState(null);
    const [categories, setCategories] = useState([]);
    const [clientDialogOpen, setClientDialogOpen] = useState(false);
    const [contractsDialogOpen, setContractsDialogOpen] = useState(false);
    const [snack, setSnack] = useState({open: false, severity: "info", message: ""});

    // Dialogs confirmation
    const [confirmDialogToCancelBookingOpen, setConfirmDialogToCancelBookingOpen] = useState(false);
    const [confirmDialogToValidateBookingOpen, setConfirmDialogToValidateBookingOpen] = useState(false);
    const [confirmDialogToSetReadyBookingOpen, setConfirmDialogToSetReadyBookingOpen] = useState(false);
    const [confirmDialogToCompleteBookingOpen, setConfirmDialogToCompleteBookingOpen] = useState(false);
    const [confirmDialogToSendReviewEmailOpen, setConfirmDialogToSendReviewEmailOpen] = useState(false);
    const [confirmDialogToSendLastContract, setConfirmDialogToSendLastContract] = useState(false);

    const [editNotesDialogOpen, setEditNotesDialogOpen] = useState(false);
    const [editNotesLoading, setEditNotesLoading] = useState(false);
    const [notes, setNotes] = useState("");
    const [loading, setLoading] = useState(true);
    const [statusList, setStatusList] = useState([]);
    const [loadingChangeStatus, setLoadingChangeStatus] = useState(false);

    const [dialogScanOpen, setDialogScanOpen] = useState(false);
    const [showScanner, setShowScanner] = useState(false);
    const [scannedImages, setScannedImages] = useState([]);
    const signedContractInputRef = useRef(null);
    const [signedContractPdf, setSignedContractPdf] = useState(null);

    const isMobile = useIsMobile();

    const updateBooking = () => {
        return api.get(`/bookings/${params.id}`)
            .then(response => setReservation(response.data))
            .catch(error => {
                console.error("Erreur lors du chargement de la réservation:", error);
                setSnack({open: true, severity: "error", message: "Erreur de chargement de la réservation."});
                navigate("/bookings");
            })
    }

    useEffect(() => {
        const promises = [];
        promises.push(api.get(`/bookings/${params.id}`).then(response => setReservation(response.data))
            .catch(error => {
                console.error("Erreur lors du chargement de la réservation:", error);
                setSnack({open: true, severity: "error", message: "Erreur de chargement de la réservation."});
                navigate("/bookings");
            }));

        promises.push(api.get('/products/categories').then(response => {
            setCategories(response.data);
            setNotes(response.data.notes || "");
        })
            .catch(error => {
                console.error("Erreur lors du chargement des catégories de produits:", error);
                setSnack({open: true, severity: "error", message: "Erreur de chargement des catégories de produits."});
            }));

        promises.push(api.get('/bookings/status-list').then(response => setStatusList(response.data))
            .catch(error => {
                console.error("Erreur lors du chargement de la liste des statuts de réservation:", error);
                setSnack({
                    open: true,
                    severity: "error",
                    message: "Erreur de chargement de la liste des statuts de réservation."
                });
            }));

        Promise.all(promises).then(() => setLoading(false));
    }, [params.id, navigate]);

    async function handleGenerateQuote() {
        setSnack({open: true, severity: "info", message: "Génération du devis..."});
        const pdfUrl = `${API_BASE_URL}/estimates/generate_estimate/${reservation.id}`;
        const newWindow = window.open(pdfUrl, "_blank");
        if (newWindow) {
            newWindow.document.title = `Devis-${reservation.id}`;
            setSnack({open: true, severity: "success", message: "Devis généré."});
            setTimeout(() => {
                api.get(`/bookings/${params.id}`).then(res => setReservation(res.data));
            }, 1000);
        } else {
            setSnack({open: true, severity: "error", message: "Impossible d'ouvrir le devis (pop-up bloquée ?)"});
        }
    }

    async function handleGenerateContract(version = 1, signed = false) {
        if (reservation.status.key === "draft") {
            setSnack({
                open: true,
                severity: "error",
                message: "Le contrat ne peut pas être généré pour une réservation en brouillon."
            });
            return;
        }
        setSnack({open: true, severity: "info", message: "Génération du contrat..."});
        const pdfUrl = `${API_BASE_URL}/contracts/get?bookingId=${reservation.id}&version=${version}&signed=${signed}`;
        const newWindow = window.open(pdfUrl, "_blank");
        if (newWindow) {
            setSnack({open: true, severity: "success", message: "Contrat généré."});
        } else {
            setSnack({open: true, severity: "error", message: "Impossible d'ouvrir le contrat (pop-up bloquée ?)"});
        }
    }

    function handleChangeStatus(nextStatus) {
        if (!statusList.find((s) => s.key === nextStatus)) {
            setSnack({open: true, severity: "error", message: `Statut inconnu: '${nextStatus}'.`});
            return;
        }

        if (loadingChangeStatus) return;
        setLoadingChangeStatus(true);

        api.put(`/bookings/edit/status/${reservation.id}`, {status: nextStatus})
            .then((res) => {
                updateBooking();
                setSnack({open: true, severity: "success", message: `Statut changé en '${res.data.label}'.`});
            })
            .catch(error => {
                console.error("Erreur lors du changement de statut:", error);
                setSnack({open: true, severity: "error", message: "Erreur lors du changement de statut."});
            })
            .finally(() => {
                setLoadingChangeStatus(false);
            });
    }

    const handleSaveNotes = () => {
        if (editNotesLoading) return;
        setEditNotesLoading(true);

        api.put(`/bookings/edit/notes/${reservation.id}`, {notes: notes})
            .then(() => {
                setReservation((prev) => ({...prev, notes: notes}));
                setEditNotesDialogOpen(false);
                setSnack({open: true, severity: "success", message: "Notes mises à jour."});
            })
            .catch(error => {
                console.error("Erreur lors de la sauvegarde des notes:", error);
                setSnack({open: true, severity: "error", message: "Erreur lors de la sauvegarde des notes."});
            })
            .finally(() => {
                setEditNotesLoading(false);
            });
    }

    const handleSendMail = (type) => {
        let path = "";
        switch (type) {
            case "estimate":
                path = `estimates/send_estimate/${reservation.id}`;
                break;
            case "deposit_invoice":
                path = `invoices/send_deposit_invoice/${reservation.id}`;
                break;
            case "final_invoice":
                path = `invoices/send_final_invoice/${reservation.id}`;
                break;
            case "contract":
                path = `contracts/send_contract/${reservation.id}`;
                break
            case "review":
                path = `bookings/send_review/${reservation.id}`;
                break;
            default:
                setSnack({open: true, severity: "error", message: "Type d'email inconnu."});
                return;
        }

        api.post(path)
            .then(() => {
                setSnack({open: true, severity: "success", message: "Email envoyé."});
            })
            .catch(error => {
                console.error("Erreur lors de l'envoi de l'email:", error);
                setSnack({open: true, severity: "error", message: "Erreur lors de l'envoi de l'email."});
            });
    }

    const handleCancelBooking = () => {
        if (loadingChangeStatus) return;

        setLoadingChangeStatus(true);

        api.post(`bookings/cancel`, {
            booking_id: reservation.id,
        })
            .then(() => {
                updateBooking().then(() => {
                    setSnack({open: true, severity: "success", message: "Réservation annulée."});
                })
            })
            .catch(error => {
                setSnack({
                    open: true,
                    severity: "error",
                    message: error.response?.data?.message || "Erreur lors de l'annulation."
                });
            })
            .finally(() => setLoadingChangeStatus(false));
    }

    const handleCapture = (images) => {
        setScannedImages(images);
        setShowScanner(false);
    };

    const handleUploadScannedContract = () => {
        if (scannedImages.length === 0 && !signedContractPdf) {
            setSnack({open: true, severity: "error", message: "Aucun document à uploader."});
            return;
        }

        const formData = new FormData();
        formData.append('booking_id', reservation.id);

        if (signedContractPdf) {
            formData.append('pdf', signedContractPdf);
        } else {
            formData.append('pages_img', JSON.stringify(scannedImages));
        }

        api.post('contracts/save_signed_contract', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })
            .then(res => {
                if (res.status === 200) {
                    setSnack({open: true, severity: "success", message: "Contrat signé sauvegardé."});
                    setScannedImages([]);
                    setShowScanner(false);
                    setDialogScanOpen(false);
                    updateBooking();
                } else {
                    setSnack({
                        open: true,
                        severity: "error",
                        message: res.data.message || "Erreur lors de la sauvegarde du contrat signé."
                    });
                }
            })
    }

    const handleSignedContractPdfChange = (event) => {
        const file = event.target.files[0];
        if (!file) return null;

        if (file.type === "application/pdf") {
            setSignedContractPdf(file);
            setScannedImages([]);
        } else {
            return null;
        }
    }

    const calculateTotal = () => {
        if (!reservation) return {totalHT: 0, totalTTC: 0};
        let totalHT = 0;
        let totalTTC = 0;


        reservation.products.forEach((bp) => {
            let productTotalHT = bp.priceAtMoment * bp.quantity;
            if (bp.discountType === 'percent') {
                productTotalHT -= productTotalHT * (bp.discountValue / 100);
            } else if (bp.discountType === 'fixed') {
                productTotalHT -= productTotalHT * (bp.discountPercent / 100);
            }
            let productTotalTTC = productTotalHT * (1 + bp.product.tva / 100);
            reservation.options.filter((bpo) => bpo.bookingProductId === bp.id).forEach((bpo) => {
                let optionTotalHT = bpo.priceAtMoment * bpo.quantity;
                if (bp.discountType === 'percent') {
                    optionTotalHT -= optionTotalHT * (bp.discountValue / 100);
                } else if (bp.discountType === 'fixed') {
                    optionTotalHT -= optionTotalHT * (bp.discountPercent / 100);
                }
                productTotalHT += optionTotalHT;
                productTotalTTC += optionTotalHT * (1 + bpo.option.tva / 100);
            })

            totalHT += productTotalHT;
            totalTTC += productTotalTTC;
        })


        return {totalHT, totalTTC};
    }

    return {
        reservation,
        setReservation,
        categories,
        loading,
        loadingChangeStatus,
        statusList,
        isMobile,
        snack,
        setSnack,
        // Dialogs states
        clientDialogOpen,
        setClientDialogOpen,
        contractsDialogOpen,
        setContractsDialogOpen,
        editNotesDialogOpen,
        setEditNotesDialogOpen,
        confirmDialogToCancelBookingOpen,
        setConfirmDialogToCancelBookingOpen,
        confirmDialogToValidateBookingOpen,
        setConfirmDialogToValidateBookingOpen,
        confirmDialogToSetReadyBookingOpen,
        setConfirmDialogToSetReadyBookingOpen,
        confirmDialogToCompleteBookingOpen,
        setConfirmDialogToCompleteBookingOpen,
        confirmDialogToSendReviewEmailOpen,
        setConfirmDialogToSendReviewEmailOpen,
        confirmDialogToSendLastContract,
        setConfirmDialogToSendLastContract,
        dialogScanOpen,
        setDialogScanOpen,
        // Scanner specific
        showScanner,
        setShowScanner,
        scannedImages,
        setScannedImages,
        signedContractPdf,
        setSignedContractPdf,
        signedContractInputRef,
        // Notes specific
        notes,
        setNotes,
        editNotesLoading,
        // Handlers
        handleGenerateQuote,
        handleGenerateContract,
        handleChangeStatus,
        handleSaveNotes,
        handleSendMail,
        handleCancelBooking,
        handleCapture,
        handleUploadScannedContract,
        handleSignedContractPdfChange,
        calculateTotal,
        updateBooking
    };
}
